/**
 * "May this person do X to this event?" — the client-side answer.
 *
 * ## Read this before using it
 *
 * These functions decide what to **render**. They are not a security boundary.
 * Every permission is checked again on the server, and that check is the one
 * that matters. If you find yourself relying on a hidden button to keep someone
 * out of something, the protection you want belongs in a Pundit policy, not
 * here.
 *
 * What this is for: not showing an operator a Pricing tab that will silently
 * discard their edits, or a Revenue link that will 403. Confusion, not access.
 *
 * The rules mirror `User#can_on?` in the Rails app:
 *   superadmin           → everything
 *   org_admin            → everything in their organization
 *   event_admin          → only assigned events, only granted permissions
 */

import type { EventPermission, User } from "@/lib/types/api";

/** Organization-wide admins. Their access isn't expressed as memberships. */
export function isOrgWide(user: User | null | undefined): boolean {
  return user?.role === "org_admin" || user?.role === "superadmin";
}

export function isEventAdmin(user: User | null | undefined): boolean {
  return user?.role === "event_admin";
}

/**
 * Whether `user` holds `permission` on the event with `eventId`.
 *
 * Note the null handling: a missing user or a missing event id is **false**,
 * never true. A permission check that defaults to "yes" when it doesn't know
 * the answer is how paywalls and admin screens leak — this codebase has already
 * been bitten once by a `?? true`.
 */
export function can(
  user: User | null | undefined,
  eventId: number | null | undefined,
  permission: EventPermission,
): boolean {
  if (!user) return false;
  if (isOrgWide(user)) return true;
  if (!isEventAdmin(user) || !eventId) return false;

  const access = user.event_access?.find((entry) => entry.event_id === eventId);
  return access?.permissions.includes(permission) === true;
}

/** Holds `permission` on at least one event — used to show or hide whole nav items. */
export function canAnywhere(
  user: User | null | undefined,
  permission: EventPermission,
): boolean {
  if (!user) return false;
  if (isOrgWide(user)) return true;
  if (!isEventAdmin(user)) return false;

  return (user.event_access ?? []).some((entry) => entry.permissions.includes(permission));
}

/** Only organization-wide admins hand out access. */
export function canManageAccess(user: User | null | undefined): boolean {
  return isOrgWide(user);
}

/** Human labels for the permission checkboxes, and why each one matters. */
export const PERMISSION_LABELS: Record<EventPermission, { label: string; hint: string }> = {
  config: { label: "Edit the booth", hint: "Branding, vibes, frames, moments — everything except prices." },
  publish: { label: "Turn the link on", hint: "Make the booth live, or take it down." },
  pricing: { label: "Set prices", hint: "What guests pay to download, print or share." },
  revenue: { label: "See revenue", hint: "Orders and takings for this event." },
  gallery: { label: "See guest photos", hint: "View and download what guests captured." },
  prints: { label: "Work the print queue", hint: "Retry and clear print jobs during the event." },
};
